import { useState, useRef, useEffect, Dispatch, SetStateAction } from "react";
import { AuthgardAdminAction, AuthgardMfaStatus, BulkActionOutcome, UserType } from "src/common/enums";
import { credentialTypeForUserType, isExpiryPast, normalizeMfaStatus, parseExpiryDate } from "src/common/util";
import { chunk, toggleSetMember } from "src/common/functions";
import { BULK_AUTHGARD_ACTIONS, MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL } from "src/common/constants";
import { executeAuthgardAdminOperation } from "src/components/auth/authgard-admin-provider";
import { AuthgardItemResult, AuthgardResultStatus } from "src/models/authgard-item-result";
import { AdminOperationSuccess, AuthgardCredentialType } from "src/models/bulk-execute-authgard-admin-operation";
import { logger } from "src/logger";

/**
 * Represents a user in the bulk action flow with their MFA state.
 */
export interface BulkUser {
    principalArn: string
    fullName: string
    userType: UserType
    currentStatus: AuthgardMfaStatus
    /**
     * Invite expiry as epoch millis, or null when there is no pending invite. The raw timestamp is
     * carried (not a precomputed boolean) so expired-ness is evaluated when it matters — a snapshot
     * taken on open would still read "not expired" after the invite lapses under an open wizard.
     */
    inviteExpiryMs: number | null
}

/**
 * Whether the user's pending invite has lapsed. Callers that make or display a decision for a whole
 * cohort must pass the pass's pinned `nowMs` (see UseBulkManageScan2AuthResult.nowMs) so every user
 * in the pass — and the render the operator saw — is judged against the same instant.
 */
export const isInviteExpired = (user: BulkUser, nowMs: number = Date.now()): boolean =>
    isExpiryPast(user.inviteExpiryMs, nowMs);

/**
 * Result of a bulk action for a single user.
 */
export interface BulkActionResult {
    user: BulkUser
    /** See BulkActionOutcome: FAILED and UNKNOWN are not interchangeable. */
    outcome: BulkActionOutcome
    /**
     * Whether the invite read as expired at the instant the run's eligibility was decided. The
     * results step renders THIS, never a live re-evaluation: a row's copy must keep matching the
     * skip reason / error recorded alongside it, even if the invite lapses while results sit open.
     */
    wasExpired: boolean
    regCode?: string
    expiry?: Date
    errorCode?: string
    newStatus?: AuthgardMfaStatus
    /**
     * Why the user was skipped. Set only when `outcome` is SKIPPED — carried through from the
     * eligibility partition so the results rows explain themselves without recomputing.
     */
    skipReason?: IneligibilityReason
}

export type ModalStep = 'select-action' | 'confirm' | 'results';

/**
 * The cohort a bulk run operates on, plus the modal's open state.
 *
 * `scope` and `credentialType` are pinned by the bulk entry the operator chose on the principal list,
 * so a run is always a single (scope, credentialType) pair. `open` is here because the modal stays
 * mounted between openings — the hook keys its reset off the transition to open so a previous run's
 * results can't bleed into the next one.
 */
export interface UseBulkManageScan2AuthArgs {
    open: boolean
    users: BulkUser[]
    scope: string
    credentialType: AuthgardCredentialType
}

/**
 * Why a selected principal isn't a valid target for the chosen action. Both keep the user out of the
 * run; they differ only in what the operator is told.
 *
 * - `credential-type` — the other Authgard credential type applies to this principal, so this action
 *                doesn't apply to them at all.
 * - `status`   — their current MFA state isn't a valid starting point for this action.
 */
export type IneligibilityReason = 'credential-type' | 'status';

export interface IneligibleUser {
    user: BulkUser
    reason: IneligibilityReason
}

export interface ActionEligibility {
    eligible: BulkUser[]
    ineligible: IneligibleUser[]
}

/**
 * How far a batched run has got, for the confirm step's progress copy. `total` is the eligible cohort;
 * `completed` counts targets whose batch has come back, however it came back.
 */
export interface RunProgress {
    completed: number
    total: number
}

/** Whether the action's state machine accepts this principal's current MFA state at `nowMs`. */
const statusAllowsAction = (action: AuthgardAdminAction, u: BulkUser, nowMs: number): boolean => {
    switch (action) {
        case AuthgardAdminAction.INITIATE:
            return u.currentStatus === AuthgardMfaStatus.NO_RECORD ||
                ((u.currentStatus === AuthgardMfaStatus.INITIATED || u.currentStatus === AuthgardMfaStatus.REINITIATED) && isInviteExpired(u, nowMs));
        case AuthgardAdminAction.REREGISTER:
            // Revoke an enrolled user, or reissue for an already-revoked one once their invite has
            // lapsed (Authgard rejects REREGISTER while a regcode is still active). Only the V2
            // spelling exists in the enum: normalizeMfaStatus collapses V1's 'RESET_FIDO2' at the
            // wire boundary.
            return u.currentStatus === AuthgardMfaStatus.ONBOARDED ||
                (u.currentStatus === AuthgardMfaStatus.REREGISTER && isInviteExpired(u, nowMs));
        case AuthgardAdminAction.CANCEL_INITIATE:
            return u.currentStatus === AuthgardMfaStatus.INITIATED ||
                u.currentStatus === AuthgardMfaStatus.REINITIATED;
        default:
            return false;
    }
};

/**
 * Splits the selected principals into those a given action will actually be sent for and those it
 * won't, each with the reason why — so the wizard can show an honest blast radius before the operator
 * commits, and distinguish "this action doesn't apply to them" from "not from their current state".
 *
 * Only `eligible` is ever sent. Applicability is checked before current state and the first match wins,
 * so a principal the other credential type applies to reads that way even if their status would also
 * have excluded them.
 *
 * The whole pass is judged against one instant: `nowMs` is sampled once (or taken from the caller),
 * never per-user, so two users with the same expiry always classify alike within a pass, and a
 * caller that renders and later executes with the same pinned `nowMs` gets the same partition.
 *
 * @param action the admin operation under consideration.
 * @param users the operator's selected principals.
 * @param context the run's credential type, plus optionally the pinned instant to judge expiry at.
 * @return the eligible cohort plus the ineligible principals and their reasons.
 */
export const partitionByEligibility = (
    action: AuthgardAdminAction,
    users: BulkUser[],
    context: { credentialType: AuthgardCredentialType, nowMs?: number },
): ActionEligibility => {
    const nowMs = context.nowMs ?? Date.now();
    const eligible: BulkUser[] = [];
    const ineligible: IneligibleUser[] = [];

    for (const user of users) {
        if (credentialTypeForUserType(user.userType) !== context.credentialType) {
            ineligible.push({ user, reason: 'credential-type' });
        } else if (!statusAllowsAction(action, user, nowMs)) {
            ineligible.push({ user, reason: 'status' });
        } else {
            eligible.push(user);
        }
    }

    return { eligible, ineligible };
};


export interface UseBulkManageScan2AuthResult {
    // state
    step: ModalStep;
    selectedAction: AuthgardAdminAction | null;
    results: BulkActionResult[];
    users: BulkUser[];
    actions: AuthgardAdminAction[];
    // True while the bulk operation is in flight (drives the Confirm button's disabled state).
    submitting: boolean;
    // Batch progress for the run in flight, so a multi-batch cohort does not look unresponsive.
    progress: RunProgress;
    expandedUsers: Set<string>;
    expandedActions: Set<string>;
    expandedIneligible: Set<string>;
    confirmCountInput: string;
    skippedExpanded: boolean;
    /**
     * The pinned instant expiry is judged against, refreshed on open and on every step transition
     * but never mid-step. Renders and handleConfirm both use it, so the cohort the operator
     * confirms is exactly the cohort that runs — a user whose invite lapses between render and
     * click cannot silently join a destructive run.
     */
    nowMs: number;
    // setters consumed directly by the presentational render (kept as-is to preserve behavior)
    setSkippedExpanded: Dispatch<SetStateAction<boolean>>;
    setConfirmCountInput: Dispatch<SetStateAction<string>>;
    // actions
    resetState: () => void;
    toggleExpand: (arn: string) => void;
    toggleActionExpand: (action: string) => void;
    toggleIneligibleExpand: (action: string) => void;
    handleSelectAction: (action: AuthgardAdminAction) => void;
    handleConfirm: () => Promise<void>;
    handleBack: () => void;
    /**
     * The eligible/ineligible split for an action, with the run's credential type already applied.
     * Exposed from the hook so the render can't accidentally compute a blast radius under different
     * assumptions than the one handleConfirm sends.
     */
    eligibilityFor: (action: AuthgardAdminAction) => ActionEligibility;
}

/**
 * Owns the bulk Authgard MFA management wizard: step navigation
 * (select-action -> confirm -> results), the selected action, the per-user results, and all the
 * expand/reveal/confirm-count UI state. The modal becomes presentational.
 *
 * The cohort is supplied by the caller (the principal list, from the operator's selection). Results
 * come from the real bulkExecuteAuthgardAdminOperation provider, served by the dev-only mock Apollo
 * link in development (see clients/dev-mock-link.ts); swapping to the live backend is a link change,
 * not a change here.
 */
export const useBulkManageScan2Auth = (args: UseBulkManageScan2AuthArgs): UseBulkManageScan2AuthResult => {
    const { open, users, scope, credentialType } = args;
    const [step, setStep] = useState<ModalStep>('select-action');
    const [selectedAction, setSelectedAction] = useState<AuthgardAdminAction | null>(null);
    const [results, setResults] = useState<BulkActionResult[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [progress, setProgress] = useState<RunProgress>({ completed: 0, total: 0 });
    // Synchronous guard against a double-run of the bulk operation while one is in flight. Deliberately
    // NOT cleared by resetState: the request outlives the wizard being closed, and clearing it would
    // let a reopened wizard dispatch a second identical write against the same cohort.
    const submittingRef = useRef<boolean>(false);
    // Identifies the current run so an abandoned one (the operator closed the wizard mid-flight) can't
    // apply its results to whatever they opened next. Bumped by resetState and by each new run.
    const runIdRef = useRef<number>(0);
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
    // For destructive bulk actions: admin must type the affected user count to enable Confirm.
    const [confirmCountInput, setConfirmCountInput] = useState<string>('');
    const [skippedExpanded, setSkippedExpanded] = useState<boolean>(false);
    // See UseBulkManageScan2AuthResult.nowMs. Refreshed at step transitions only, so within a step
    // every render and the eventual confirm all judge expiry against the same instant.
    const [nowMs, setNowMs] = useState<number>(() => Date.now());
    const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
    // Per-action collapse of the "Not eligible" sub-list — collapsed by default so a
    // large ineligible set doesn't dominate the action row when it's expanded.
    const [expandedIneligible, setExpandedIneligible] = useState<Set<string>>(new Set());

    const actions = BULK_AUTHGARD_ACTIONS;

    // The modal stays mounted while closed, so an in-flight run can outlive the operator dismissing
    // it. Track mounted-ness to avoid setState on a torn-down tree if the page itself unmounts.
    const mountedRef = useRef<boolean>(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const resetState = () => {
        // Orphan any run still in flight so its results can't land on the wizard the operator opens
        // next. submittingRef stays as it is — the request is still outstanding, and only its own
        // settling may clear it (see the ref's declaration).
        runIdRef.current += 1;
        setStep('select-action');
        setSelectedAction(null);
        setResults([]);
        // Mirror the ref rather than clearing: an abandoned run keeps submittingRef.current true until
        // it settles, and handleConfirm refuses while it is. Setting false here would render Confirm
        // enabled on the reopened wizard while every click silently no-ops.
        setSubmitting(submittingRef.current);
        setProgress({ completed: 0, total: 0 });
        setExpandedUsers(new Set());
        setConfirmCountInput('');
        setSkippedExpanded(false);
        setNowMs(Date.now());
        setExpandedActions(new Set());
        setExpandedIneligible(new Set());
    };

    // Start every opening from a clean wizard. Without this, closing on the results step (or mid-run)
    // leaves the previous run's step/results in place and the next opening resumes into someone
    // else's outcome — the modal is never unmounted, so nothing else clears it.
    useEffect(() => {
        if (open) {
            resetState();
        }
    }, [open]);

    const toggleExpand = (arn: string) => setExpandedUsers(prev => toggleSetMember(prev, arn));

    const handleSelectAction = (action: AuthgardAdminAction) => {
        setSelectedAction(action);
        setConfirmCountInput('');
        setNowMs(Date.now());
        setStep('confirm');
    };

    const eligibilityFor = (action: AuthgardAdminAction): ActionEligibility =>
        partitionByEligibility(action, users, { credentialType, nowMs });

    /**
     * Runs the selected action against the eligible cohort and maps the per-principal outcomes into
     * results. Ineligible users are marked 'skipped' and never sent.
     *
     * PCS accepts at most MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL targets per call, so a larger cohort is sent as
     * consecutive batches in order — the cohort is never capped, and the operator never has to split a
     * site by hand. Batches are sequential, not concurrent: several simultaneous revokes against one
     * site buys nothing and makes the failure story harder to read.
     *
     * A batch that throws marks only its own targets failed and the run continues, so an earlier
     * batch's real outcomes survive. That means a run can partially apply — which the per-principal
     * results table reports exactly, rather than the alternative of declaring every target failed
     * because one batch died.
     */
    const handleConfirm = async (): Promise<void> => {
        if (!selectedAction || submittingRef.current) {
            return;
        }
        // Same nowMs as every confirm-step render, so this partition is exactly the blast radius the
        // operator was shown — an invite lapsing between render and click can't grow the cohort.
        const { eligible, ineligible } = eligibilityFor(selectedAction);
        const eligibleArns = new Set(eligible.map(u => u.principalArn));
        const skipReasonByArn = new Map(ineligible.map(i => [i.user.principalArn, i.reason]));
        // Batched up front, before this run claims any state: chunk throws on a misconfigured batch
        // size, and failing here leaves the wizard untouched instead of stranding submittingRef true
        // or settling into an empty results step.
        const batches = chunk(eligible, MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL);
        // Claim this run. A later resetState bumps the id, which orphans everything below.
        const runId = ++runIdRef.current;
        const isCurrentRun = (): boolean => mountedRef.current && runId === runIdRef.current;
        submittingRef.current = true;
        setSubmitting(true);
        setProgress({ completed: 0, total: eligible.length });

        // Accumulated across batches; a target left out of the map reads as an unknown outcome below.
        const resultByArn = new Map<string, AuthgardItemResult<AdminOperationSuccess>>();
        try {
            for (const batch of batches) {
                try {
                    const batchResults = await executeAuthgardAdminOperation({
                        scope,
                        operation: selectedAction,
                        credentialType,
                        targets: batch.map(u => ({ principalArn: u.principalArn, userType: u.userType })),
                    });
                    batchResults.forEach((result, arn) => resultByArn.set(arn, result));
                } catch (e) {
                    // This batch's outcome is unknown. Leave its targets out of the map so they render
                    // as failures, and carry on with the remaining batches. Logged because the provider
                    // only logs its own transport errors.
                    logger.error(`Bulk ${selectedAction} batch of ${batch.length} target(s) failed`, e as Error);
                }
                // Stop as soon as the operator abandons the wizard; the batches already sent stand, but
                // nothing further is dispatched and no state is written for a cohort they left.
                if (!isCurrentRun()) {
                    return;
                }
                setProgress(prev => ({ completed: prev.completed + batch.length, total: eligible.length }));
            }

            setResults(users.map((user): BulkActionResult => {
                // Snapshotted at the run's decision instant — see BulkActionResult.wasExpired.
                const wasExpired = isInviteExpired(user, nowMs);
                if (!eligibleArns.has(user.principalArn)) {
                    return { user, outcome: BulkActionOutcome.SKIPPED, wasExpired, skipReason: skipReasonByArn.get(user.principalArn) };
                }
                const result = resultByArn.get(user.principalArn);
                // Missing (its batch threw), or UNKNOWN — either way the outcome is undetermined, so
                // surface it as a failure, never success.
                if (!result || result.status === AuthgardResultStatus.UNKNOWN) {
                    // No result for a target we sent: the write may have partially applied, so this is
                    // NOT a failure the operator can safely retry. See BulkActionResult.outcome.
                    return { user, outcome: BulkActionOutcome.UNKNOWN, wasExpired };
                }
                if (result.status === AuthgardResultStatus.FAILURE) {
                    return { user, outcome: BulkActionOutcome.FAILED, wasExpired, errorCode: result.errorCode };
                }
                return {
                    user,
                    outcome: BulkActionOutcome.SUCCESS,
                    wasExpired,
                    regCode: result.value.regCode ?? undefined,
                    expiry: parseExpiryDate(result.value.mfaExpiry),
                    newStatus: result.value.userStatus ? normalizeMfaStatus(result.value.userStatus) : undefined,
                };
            }));
        } finally {
            // Always clear: the run has settled, so a fresh one is allowed even if this was orphaned.
            // The step/submitting writes only apply if the wizard is still on this run.
            submittingRef.current = false;
            // Clearing `submitting` is gated on being mounted, not on being the current run: an
            // orphaned run still has to release the button, or a wizard reopened mid-flight (which
            // seeded `submitting` from the ref in resetState) would stay disabled forever. Advancing
            // the step stays gated on the run, so orphaned results never land on a later wizard.
            if (mountedRef.current) {
                setSubmitting(false);
            }
            if (isCurrentRun()) {
                setStep('results');
            }
        }
    };

    // Returns to action selection. Refused while a run is in flight: clearing selectedAction does not
    // orphan the run (only resetState bumps the id), so the run would still settle into
    // setStep('results') — with no action left for the results step to render, stranding its outcomes.
    // The Back button is disabled for the same reason; this is the guard for any other caller.
    const handleBack = () => {
        if (submittingRef.current) {
            return;
        }
        setStep('select-action');
        setSelectedAction(null);
        setConfirmCountInput('');
        setNowMs(Date.now());
    };

    const toggleActionExpand = (action: string) => setExpandedActions(prev => toggleSetMember(prev, action));

    const toggleIneligibleExpand = (action: string) => setExpandedIneligible(prev => toggleSetMember(prev, action));

    return {
        step,
        selectedAction,
        results,
        users,
        actions,
        submitting,
        progress,
        expandedUsers,
        expandedActions,
        expandedIneligible,
        confirmCountInput,
        skippedExpanded,
        nowMs,
        setSkippedExpanded,
        setConfirmCountInput,
        resetState,
        toggleExpand,
        toggleActionExpand,
        toggleIneligibleExpand,
        handleSelectAction,
        handleConfirm,
        handleBack,
        eligibilityFor,
    };
};
